import { useContext, useEffect, useMemo } from 'react';
import { TabContext } from 'console/navigation/context/type';
export const useKeybind = () => {
    const { selected_tab } = useContext(TabContext);
    const keyupHandler = useMemo(() => (e) => {
        if (e.target instanceof HTMLInputElement ||
            e.target instanceof HTMLTextAreaElement)
            return;
        const parent = document.querySelector(`[data-tab_key="${selected_tab.key}"]`);
        if (!parent)
            return;
        if (e.shiftKey) {
            let element;
            switch (e.code) {
                case 'KeyH':
                    element = getElementByDataTooltip(parent, 'heating-circuit-card');
                    break;
                case 'KeyB':
                    element = getElementByDataTooltip(parent, 'boiler-card');
                    break;
                case 'KeyC':
                    element = getElementByDataTooltip(parent, 'custom-control-card');
                    break;
                case 'KeyS':
                    element = getElementByDataTooltip(parent, 'sensor-card');
                    break;
                case 'KeyT':
                    element = getElementByDataTooltip(parent, 'thermometer-card');
                    break;
                case 'KeyR':
                    element = getElementByDataTooltip(parent, 'rf-indicator-card');
                    break;
                case 'KeyV':
                    element = getElementByDataTooltip(parent, 'vehicle-control-card');
                    break;
                case 'KeyZ':
                    element = getElementByDataTooltip(parent, 'guard-zone-card');
                    break;
                case 'KeyG':
                    element = getElementByDataTooltip(parent, 'guard-control-card');
                    break;
                case 'KeyM':
                    element = getElementByDataTooltip(parent, 'heating_modes-card')?.querySelector('.heating-mode-button');
                    break;
                case 'KeyN':
                    element = getElementByDataTooltip(parent, 'boiler_heating_modes-card')?.querySelector('.heating-mode-button');
                    break;
                default:
            }
            if (element) {
                element?.focus();
            }
        }
        if (e)
            if (e.target?.tabIndex !== -2)
                return;
        if (e.code === 'Enter') {
            const inner_button = e.target.querySelector('[tabindex="-2"]');
            if (inner_button) {
                inner_button.click();
                return;
            }
            e.target.click();
        }
        const sortable_card_elem = e.target?.closest('.sortable-card');
        switch (e.code) {
            case 'ArrowUp':
                moveTop(sortable_card_elem, e.target);
                break;
            case 'ArrowRight':
                moveRight(sortable_card_elem, e.target);
                break;
            case 'ArrowDown':
                moveDown(sortable_card_elem, e.target);
                break;
            case 'ArrowLeft':
                moveLeft(sortable_card_elem, e.target);
                break;
            default:
        }
    }, [selected_tab]);
    useEffect(() => {
        document.addEventListener('keyup', keyupHandler);
        return () => document.removeEventListener('keyup', keyupHandler);
    }, [selected_tab]);
};
function getElementByDataTooltip(parent, data_tooltip) {
    if (!parent)
        return;
    const element = parent.querySelector(`[data-tooltip=${data_tooltip}]`);
    if (!element)
        return;
    return element;
}
function moveTop(parent, target) {
    const { map, target_coord } = getChildrenMap(parent, getGoodTarget(target));
    const row_index = target_coord[0];
    const column_index = target_coord[1];
    if (map?.[row_index - 1]?.[column_index] !== 0)
        return;
    let index = column_index;
    for (let i = 0; i < row_index - 1; i++) {
        index += map[i].length;
    }
    parent.children?.[index].querySelector('[tabindex="-2"]')?.focus();
}
function moveRight(parent, target) {
    const { map, target_coord } = getChildrenMap(parent, getGoodTarget(target));
    const row_index = target_coord[0];
    const column_index = target_coord[1];
    if (map?.[row_index]?.[column_index + 1] !== 0)
        return;
    let index = column_index + 1;
    for (let i = 0; i < row_index; i++) {
        index += map[i].length;
    }
    parent.children?.[index].querySelector('[tabindex="-2"]')?.focus();
}
function moveDown(parent, target) {
    const { map, target_coord } = getChildrenMap(parent, getGoodTarget(target));
    const row_index = target_coord[0];
    const column_index = target_coord[1];
    if (map?.[row_index + 1]?.[column_index] !== 0)
        return;
    let index = column_index;
    for (let i = 0; i < row_index + 1; i++) {
        index += map[i].length;
    }
    parent.children?.[index].querySelector('[tabindex="-2"]')?.focus();
}
function moveLeft(parent, target) {
    const { map, target_coord } = getChildrenMap(parent, getGoodTarget(target));
    const row_index = target_coord[0];
    const column_index = target_coord[1];
    if (map?.[row_index]?.[column_index - 1] !== 0)
        return;
    let index = column_index - 1;
    for (let i = 0; i < row_index; i++) {
        index += map[i].length;
    }
    parent.children?.[index].querySelector('[tabindex="-2"]')?.focus();
}
function getChildrenMap(parent, target) {
    const map = [[]];
    let row_index = 0;
    const target_coord = [0, 0];
    if (!parent?.children)
        return { map, target_coord };
    if (parent?.children?.length === 0)
        return { map, target_coord };
    if (Math.abs(target.offsetLeft - parent.children[0]?.offsetLeft) <
        20 &&
        Math.abs(target.offsetTop - parent.children[0]?.offsetTop) < 20) {
        map[0].push(1);
    }
    else {
        map[0].push(0);
    }
    for (let i = 1; i < parent.children.length; i++) {
        const is_new_line = parent.children[i]?.offsetTop !==
            parent.children[i - 1]?.offsetTop;
        if (is_new_line) {
            row_index++;
            map.push([]);
        }
        if (target.offsetLeft === parent.children[i]?.offsetLeft &&
            target.offsetTop === parent.children[i]?.offsetTop) {
            map[row_index].push(1);
            target_coord[0] = row_index;
            target_coord[1] = map[row_index].length - 1;
        }
        else {
            map[row_index].push(0);
        }
    }
    return {
        map,
        target_coord,
    };
}
function getGoodTarget(parent) {
    let current_parent = parent;
    const sortable_card = current_parent.closest('.sortable-card');
    if (!sortable_card)
        return current_parent;
    let i = 0;
    while (i < 10) {
        if (!current_parent?.parentElement)
            break;
        if (current_parent?.parentElement?.isEqualNode(sortable_card)) {
            break;
        }
        current_parent = current_parent?.parentElement;
        i++;
    }
    return current_parent;
}
